
import { supabase } from "@/integrations/supabase/client";

/**
 * Saves mock test results to both localStorage and the database
 * @param results The test results to save
 * @returns A promise that resolves to true if the database save was successful
 */
export async function saveMockTestResults(results: any): Promise<boolean> {
  try {
    // Always save to localStorage first (preserving existing functionality)
    localStorage.setItem(`mockTestResult_${results.id}`, JSON.stringify(results));
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log("User not authenticated, only saved to localStorage");
      return false;
    }
    
    // Prepare data for database insert
    const dbRecord = {
      user_id: user.id,
      test_id: results.testId,
      selected_answers: results.submittedAnswers,
      test_config: results.testConfig,
      total_questions: results.totalPossibleScore,
      correct_answers: results.score,
      score: results.score,
      total_possible_score: results.totalPossibleScore,
      percentage: results.percentage,
      time_spent: results.timeSpent,
      cognitive_data: results.cognitiveData,
      completed_at: results.completedAt
    };
    
    // Save to database
    const { error } = await supabase
      .from('mock_test_attempts')
      .insert(dbRecord);
      
    if (error) {
      console.error("Error saving mock test results to database:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in saveMockTestResults:", error);
    return false;
  }
}

/**
 * Retrieves a mock test result by ID
 * First tries localStorage, then falls back to database if not found
 * @param resultId The result ID to fetch
 * @returns The test result or null if not found
 */
export async function getMockTestResult(resultId: string): Promise<any> {
  try {
    // First check localStorage
    const localResult = localStorage.getItem(`mockTestResult_${resultId}`);
    
    if (localResult) {
      return JSON.parse(localResult);
    }
    
    // If not in localStorage, try database
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log("User not authenticated, can't check database");
      return null;
    }
    
    const { data, error } = await supabase
      .from('mock_test_attempts')
      .select('*')
      .eq('id', resultId)
      .eq('user_id', user.id)
      .single();
      
    if (error || !data) {
      console.error("Error fetching mock test result from database:", error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error("Error in getMockTestResult:", error);
    return null;
  }
}

/**
 * Gets all mock test results for the current user
 * @param timeframe Optional timeframe filter ('week', 'month', 'all')
 * @returns Array of test results
 */
export async function getAllMockTestResults(timeframe: 'week' | 'month' | 'all' = 'all'): Promise<any[]> {
  try {
    const results: any[] = [];
    
    // Calculate date range based on timeframe
    const now = new Date();
    let startDate = new Date();
    
    if (timeframe === 'week') {
      startDate.setDate(now.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate = new Date(2000, 0, 1); // A long time ago for 'all'
    }
    
    // Create a Set of IDs to track what we've already added
    const existingIds = new Set<string>();
    
    // First, collect all localStorage results
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mockTestResult_')) {
        try {
          const result = JSON.parse(localStorage.getItem(key) || '');
          
          // Extract the ID and add to tracking set
          const resultId = result.id?.toString();
          if (resultId) {
            existingIds.add(resultId);
          }
          
          // Apply timeframe filter if completedAt exists
          if (result.completedAt) {
            const completedDate = new Date(result.completedAt);
            if (completedDate >= startDate && completedDate <= now) {
              results.push(result);
            }
          } else {
            // Include if no timestamp (legacy data)
            results.push(result);
          }
        } catch (e) {
          console.error("Error parsing localStorage item:", e);
        }
      }
    }
    
    // Then, get database results
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      const { data, error } = await supabase
        .from('mock_test_attempts')
        .select('*')
        .eq('user_id', user.id)
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', now.toISOString())
        .order('completed_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching mock test results from database:", error);
      } else if (data) {
        // Add database results, avoiding duplicates
        for (const dbResult of data) {
          const dbResultId = dbResult.id?.toString();
          // Only add if not already in the results array
          if (dbResultId && !existingIds.has(dbResultId)) {
            existingIds.add(dbResultId);
            results.push(dbResult);
          }
        }
      }
    }
    
    // Sort by completedAt/created_at descending
    return results.sort((a, b) => {
      const dateA = new Date(a.completedAt || a.completed_at);
      const dateB = new Date(b.completedAt || b.completed_at);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error("Error in getAllMockTestResults:", error);
    return [];
  }
}
